<template>
  <div class="flow-designer">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-button size="small" icon="el-icon-back" @click="handleBack">返回</el-button>
        <span class="flow-title">{{ flowDef.title || '流程设计' }}</span>
        <el-tag v-if="currentVersion" size="small" style="margin-left: 10px;">V{{ currentVersion }}</el-tag>
        <el-tag v-if="mode === 'edit'" size="small" type="warning" style="margin-left: 6px;">编辑模式</el-tag>
        <el-tag v-if="mode === 'copy' && logId" size="small" type="info" style="margin-left: 6px;">复制为新版本</el-tag>
        <el-tag v-if="mode === 'copy' && !logId" size="small" type="success" style="margin-left: 6px;">新建版本</el-tag>
      </div>
      <div class="toolbar-right">
        <el-button size="small" icon="el-icon-upload2" @click="handleImport">导入JSON</el-button>
        <el-button size="small" icon="el-icon-download" @click="handleExport">导出JSON</el-button>
        <el-button size="small" type="success" icon="el-icon-check" @click="handleSave">{{ mode === 'copy' ? (logId ? '保存为新版本' : '保存') : '保存' }}</el-button>
      </div>
    </div>

    <div class="designer-container">
      <!-- 左侧：表单字段树 -->
      <div class="left-panel" :style="{ width: leftPanelWidth + 'px' }">
        <div class="panel-header">
          <span>表单定义</span>
          <el-button type="text" icon="el-icon-plus" @click="handleAddFieldGroup">添加</el-button>
        </div>
        <div class="panel-content">
          <el-tree
            ref="fieldTree"
            :data="formFields"
            :props="treeProps"
            node-key="_uid"
            default-expand-all
            :expand-on-click-node="false"
            draggable
            :allow-drop="handleAllowDrop"
            :allow-drag="handleAllowDrag"
          >
            <span slot-scope="{ data }" class="tree-node">
              <span class="node-label">
                <i :class="getFieldIcon(data)" style="margin-right: 4px;"></i>
                {{ data.title }}
              </span>
              <span class="node-actions">
                <i v-if="data.type === 'Object' || data.type === 'Array'" class="el-icon-plus" title="添加子字段" @click.stop="handleAddField(data)"></i>
                <i class="el-icon-edit" title="编辑" @click.stop="handleEditField(data)"></i>
                <i class="el-icon-delete" title="删除" @click.stop="handleDeleteField(data)"></i>
              </span>
            </span>
          </el-tree>
        </div>
      </div>

      <!-- 左侧拖拽手柄 -->
      <div class="resize-handle" @mousedown="startResizeLeft"></div>

      <!-- 中间：流程图画布 -->
      <div class="center-panel">
        <div class="canvas-toolbar">
          <div>
            <el-button-group>
              <el-button size="mini" icon="el-icon-zoom-in" @click="handleZoomIn"></el-button>
              <el-button size="mini" icon="el-icon-zoom-out" @click="handleZoomOut"></el-button>
              <el-button size="mini" icon="el-icon-rank" @click="handleFitView">适应</el-button>
            </el-button-group>
          </div>
          <div>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDeleteSelected" plain>删除选中</el-button>
            <el-button size="mini" type="primary" icon="el-icon-plus" @click="handleAddNode">添加节点</el-button>
          </div>
        </div>
        <div id="lf-container" ref="lfContainer" class="lf-container"></div>
      </div>

      <!-- 右侧拖拽手柄 -->
      <div class="resize-handle" @mousedown="startResizeRight"></div>

      <!-- 右侧：节点配置面板 -->
      <div class="right-panel" :style="{ width: rightPanelWidth + 'px' }">
        <div v-if="selectedNode" class="panel-inner">
          <div class="panel-header">
            <span>节点配置 - {{ selectedNode.title }}</span>
          </div>
          <div class="panel-content">
            <el-form label-position="top" size="small">
              <el-collapse v-model="activeCollapse">
                <!-- 基本信息 -->
                <el-collapse-item title="基本信息" name="basic">
                  <el-form-item label="节点标识（name）">
                    <el-input v-model="selectedNode.name" placeholder="英文标识" @change="onNodeNameChange"></el-input>
                  </el-form-item>
                  <el-form-item label="节点名称（title）">
                    <el-input v-model="selectedNode.title" placeholder="显示名称" @change="onNodeTitleChange"></el-input>
                  </el-form-item>
                </el-collapse-item>

                <!-- 网关设置（仅网关节点显示） -->
                <el-collapse-item v-if="selectedNode.nodeType === 'gateway'" title="网关设置" name="gateway">
                  <el-form-item label="网关类型">
                    <el-radio-group v-model="selectedNode.gatewayType">
                      <el-radio-button label="exclusive">排他</el-radio-button>
                      <el-radio-button label="parallel">并行</el-radio-button>
                      <el-radio-button label="inclusive">包容</el-radio-button>
                    </el-radio-group>
                  </el-form-item>
                  <div style="font-size: 12px; color: #909399; margin-bottom: 8px;">
                    <span v-if="selectedNode.gatewayType === 'exclusive'">排他网关：按条件只走一条分支</span>
                    <span v-else-if="selectedNode.gatewayType === 'parallel'">并行网关：所有分支同时执行，全部完成后汇合</span>
                    <span v-else-if="selectedNode.gatewayType === 'inclusive'">包容网关：满足条件的分支都执行，全部完成后汇合</span>
                  </div>
                </el-collapse-item>

                <!-- 操作设置（非网关节点显示） -->
                <el-collapse-item v-if="selectedNode.nodeType !== 'gateway'" title="操作设置" name="settings">
                  <el-form-item label="操作类型">
                    <el-select v-model="selectedNode.operatorType" style="width: 100%;">
                      <el-option label="任意一人处理" value="one"></el-option>
                      <el-option label="所有人处理（会签）" value="all"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="可执行操作">
                    <div>
                      <el-checkbox v-model="selectedNode.canSubmit">可提交</el-checkbox>
                      <el-checkbox v-model="selectedNode.canReject">可退回</el-checkbox>
                      <el-checkbox v-model="selectedNode.canEnd">可终止</el-checkbox>
                    </div>
                  </el-form-item>
                  <el-form-item label="自动提交">
                    <el-select v-model="selectedNode.autoSubmit" style="width: 100%;" clearable>
                      <el-option label="不自动提交（默认）" value=""></el-option>
                      <el-option label="静默自动提交（无记录）" value="silent"></el-option>
                      <el-option label="自动提交并记录" value="record"></el-option>
                    </el-select>
                    <div class="field-hint">配置到达该节点时是否自动提交通过</div>
                  </el-form-item>
                  <el-form-item label="列表操作文案（可选）">
                    <el-input v-model="selectedNode.actionLabel" clearable placeholder="留空则列表显示「审批」，如：用印"></el-input>
                    <div class="field-hint">仅影响待办列表入口按钮与办理页标题；配置在 defJson 节点上的 actionLabel 字段</div>
                  </el-form-item>
                  <el-form-item label="处理人重叠处理方式">
                    <el-select v-model="selectedNode.skipDuplicateOperator" style="width: 100%;" clearable>
                      <el-option label="不跳过（默认）" value=""></el-option>
                      <el-option label="静默跳过（无记录）" value="silent"></el-option>
                      <el-option label="跳过并记录" value="record"></el-option>
                    </el-select>
                    <div class="field-hint">配置当处理人与上一节点重复时的处理方式</div>
                  </el-form-item>
                </el-collapse-item>

                <!-- 操作人员（非网关节点） -->
                <el-collapse-item v-if="selectedNode.nodeType !== 'gateway'" title="操作人员" name="operators">
                  <div v-for="(op, index) in selectedNode.operators" :key="index" class="config-card">
                    <div class="config-card-header">
                      <span>操作人 {{ index + 1 }}</span>
                      <el-button type="text" icon="el-icon-close" class="delete-btn" @click="removeOperator(index)"></el-button>
                    </div>
                    <el-form-item label="指定用户">
                      <el-select v-model="op.userId" placeholder="选择用户..." clearable filterable style="width: 100%;">
                        <el-option-group v-for="group in usersByDept" :key="group.deptId" :label="group.deptName">
                          <el-option v-for="u in group.users" :key="u.id" :label="u.nickname || u.username" :value="u.id">
                            <span>{{ u.nickname || u.username }}</span>
                            <span style="float: right; color: #909399; font-size: 12px;">{{ u.username }}</span>
                          </el-option>
                        </el-option-group>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="指定角色">
                      <el-select v-model="op.role" placeholder="选择角色..." clearable filterable style="width: 100%;">
                        <el-option v-for="r in roleList" :key="r.id" :label="r.roleName" :value="String(r.id)">
                          <span>{{ r.roleName }}</span>
                          <span v-if="r.roleCode" style="float: right; color: #909399; font-size: 12px;">{{ r.roleCode }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="指定部门">
                      <el-select v-model="op.department" placeholder="选择部门..." clearable filterable style="width: 100%;">
                        <el-option v-for="d in departmentList" :key="d.id" :label="d.deptName" :value="String(d.id)">
                          <span>{{ d.deptName }}</span>
                          <span v-if="d.deptCode" style="float: right; color: #909399; font-size: 12px;">{{ d.deptCode }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="特殊部门类型">
                      <el-select v-model="op.departmentType" placeholder="选择..." clearable style="width: 100%;">
                        <el-option label="申请人所在部门" value="@applicantDept"></el-option>
                        <el-option label="部门负责人" value="@deptManager"></el-option>
                        <el-option label="分管领导" value="@deptLeader"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="从表单变量获取">
                      <el-switch v-model="op.userFromVariable"></el-switch>
                    </el-form-item>
                    <el-form-item v-if="op.userFromVariable" label="变量路径">
                      <el-input v-model="op.userVariable" placeholder="如 businessOperators.yeWu"></el-input>
                    </el-form-item>
                  </div>
                  <el-button type="primary" icon="el-icon-plus" plain size="mini" style="width: 100%;" @click="addOperator">添加操作人</el-button>
                </el-collapse-item>

                <!-- 流程分支 -->
                <el-collapse-item title="流程分支（gotos）" name="gotos">
                  <div v-for="(gt, index) in selectedNode.gotos" :key="index" class="config-card">
                    <div class="config-card-header">
                      <span>{{ gt.name || ('分支 ' + (index + 1)) }}</span>
                      <el-button type="text" icon="el-icon-close" class="delete-btn" @click="removeGoto(index)"></el-button>
                    </div>
                    <el-form-item label="分支名称">
                      <el-input v-model="gt.name" placeholder="如：同意、拒绝" @blur="onGotoNameChange(gt)"></el-input>
                    </el-form-item>
                    <el-form-item label="目标节点">
                      <el-select v-model="gt.to" placeholder="选择目标节点" style="width: 100%;" @change="(val) => onGotoTargetChange(gt, val, index)">
                        <el-option v-for="n in allNodes" :key="n.name" :label="n.title + ' (' + n.name + ')'" :value="n.name"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="条件">
                      <el-input v-model="gt.conditionText" placeholder="如：$status == 'no'"></el-input>
                      <div class="field-hint">留空表示无条件（默认流转）</div>
                    </el-form-item>
                  </div>
                  <el-button type="primary" icon="el-icon-plus" plain size="mini" style="width: 100%;" @click="addGoto">添加分支</el-button>
                </el-collapse-item>

                <!-- 表单权限（非网关节点） -->
                <el-collapse-item v-if="selectedNode.nodeType !== 'gateway'" title="表单控制（fieldsAccess）" name="fieldsAccess">
                  <!-- 快捷操作 -->
                  <div class="access-quick-actions">
                    <el-button type="text" size="mini" @click="setAllGroupsAccess(true)">全部可见</el-button>
                    <el-button type="text" size="mini" @click="setAllGroupsAccess(false)">全部隐藏</el-button>
                  </div>
                  <div v-for="group in formFields" :key="group.name" class="access-group">
                    <!-- 字段组（模块）级别 -->
                    <div class="access-group-header" @click="toggleAccessExpand(group.name)">
                      <span class="access-expand-icon">
                        <i :class="accessExpandMap[group.name] ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
                      </span>
                      <span class="access-group-title-text">{{ group.title }}</span>
                      <span class="access-group-tag" v-if="group.type">{{ group.type }}</span>
                    </div>
                    <div class="access-options access-options-group">
                      <el-checkbox
                        :value="getAccess(group.name, 'visible')"
                        @change="val => setAccess(group.name, 'visible', val)"
                      >可见</el-checkbox>
                      <el-checkbox
                        :value="getAccess(group.name, 'editable')"
                        @change="val => setAccess(group.name, 'editable', val)"
                      >可编辑</el-checkbox>
                      <el-checkbox
                        :value="getAccess(group.name, 'verifiable')"
                        @change="val => setAccess(group.name, 'verifiable', val)"
                      >必填校验</el-checkbox>
                    </div>
                    <!-- 子字段级别 -->
                    <div v-if="accessExpandMap[group.name] && group.fields && group.fields.length > 0" class="access-children">
                      <div v-for="field in group.fields" :key="field.name" class="access-field">
                        <div class="access-field-label">
                          <i :class="getFieldIcon(field)" style="margin-right: 4px; font-size: 12px; color: #909399;"></i>
                          <span>{{ field.title }}</span>
                          <span class="access-field-name">({{ field.name }})</span>
                        </div>
                        <div class="access-options access-options-field">
                          <el-checkbox
                            :value="getFieldAccess(group.name, field.name, 'visible')"
                            @change="val => setFieldAccess(group.name, field.name, 'visible', val)"
                            size="mini"
                          >可见</el-checkbox>
                          <el-checkbox
                            :value="getFieldAccess(group.name, field.name, 'editable')"
                            @change="val => setFieldAccess(group.name, field.name, 'editable', val)"
                            size="mini"
                          >可编辑</el-checkbox>
                          <el-checkbox
                            :value="getFieldAccess(group.name, field.name, 'verifiable')"
                            @change="val => setFieldAccess(group.name, field.name, 'verifiable', val)"
                            size="mini"
                          >必填校验</el-checkbox>
                        </div>
                      </div>
                    </div>
                  </div>
                </el-collapse-item>

                <!-- 观察人员（非网关节点） -->
                <el-collapse-item v-if="selectedNode.nodeType !== 'gateway'" title="观察人员（viewers）" name="viewers">
                  <div v-for="(v, index) in selectedNode.viewers" :key="index" class="config-card">
                    <div class="config-card-header">
                      <span>观察人 {{ index + 1 }}</span>
                      <el-button type="text" icon="el-icon-close" class="delete-btn" @click="removeViewer(index)"></el-button>
                    </div>
                    <el-form-item label="角色">
                      <el-select v-model="v.role" placeholder="选择角色..." clearable filterable style="width: 100%;">
                        <el-option v-for="r in roleList" :key="r.id" :label="r.roleName" :value="String(r.id)">
                          <span>{{ r.roleName }}</span>
                          <span v-if="r.roleCode" style="float: right; color: #909399; font-size: 12px;">{{ r.roleCode }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="部门">
                      <el-select v-model="v.department" placeholder="选择部门..." clearable filterable style="width: 100%;">
                        <el-option v-for="d in departmentList" :key="d.id" :label="d.deptName" :value="String(d.id)">
                          <span>{{ d.deptName }}</span>
                          <span v-if="d.deptCode" style="float: right; color: #909399; font-size: 12px;">{{ d.deptCode }}</span>
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </div>
                  <el-button type="primary" icon="el-icon-plus" plain size="mini" style="width: 100%;" @click="addViewer">添加观察人</el-button>
                </el-collapse-item>
              </el-collapse>
            </el-form>
          </div>
        </div>
        <div v-else class="panel-inner">
          <div class="panel-header"><span>节点配置</span></div>
          <div class="panel-content panel-empty">
            <i class="el-icon-info"></i>
            <p>点击流程图中的节点进行配置</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑字段对话框 -->
    <el-dialog :title="fieldDialogTitle" :visible.sync="fieldDialogVisible" width="600px" append-to-body :close-on-click-modal="false">
      <el-form ref="fieldForm" :model="fieldTemp" :rules="fieldRules" label-width="90px" size="small">
        <!-- 基本信息 -->
        <div class="field-dialog-section">
          <div class="field-dialog-section-title">基本信息</div>
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="字段标识" prop="name">
                <el-input v-model="fieldTemp.name" placeholder="英文，如 subject"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="显示名称" prop="title">
                <el-input v-model="fieldTemp.title" placeholder="如 事项"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 类型配置 -->
        <div class="field-dialog-section">
          <div class="field-dialog-section-title">类型配置</div>
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="数据类型" prop="type">
                <el-select v-model="fieldTemp.type" style="width: 100%;" @change="onFieldTypeChange">
                  <el-option label="string（文本）" value="string"></el-option>
                  <el-option label="int（整数）" value="int"></el-option>
                  <el-option label="long（长整数）" value="long"></el-option>
                  <el-option label="double（小数）" value="double"></el-option>
                  <el-option label="boolean（布尔）" value="boolean"></el-option>
                  <el-option label="date（日期）" value="date"></el-option>
                  <el-option label="datetime（日期时间）" value="datetime"></el-option>
                  <el-option label="Object（字段组）" value="Object"></el-option>
                  <el-option label="Array（可重复组）" value="Array"></el-option>
                  <el-option label="Component（自定义组件）" value="Component"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <!-- 输入方式：非 Object/Array/Component 时显示 -->
            <el-col :span="12" v-if="!['Object','Array','Component'].includes(fieldTemp.type)">
              <el-form-item label="输入方式">
                <el-select v-model="fieldTemp.inputType" style="width: 100%;" clearable>
                  <el-option v-for="opt in inputTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 选项列表：select/radio/checkbox 时显示 -->
          <el-form-item v-if="['select','radio','checkbox'].includes(fieldTemp.inputType)" label="选项列表">
            <el-input v-model="fieldTemp.inputTypeList" placeholder="用逗号分隔，如：公章,合同章,法人章">
              <template slot="append">
                <el-tooltip content="多个选项用英文逗号分隔" placement="top">
                  <i class="el-icon-info"></i>
                </el-tooltip>
              </template>
            </el-input>
            <div class="field-dialog-tags" v-if="fieldTemp.inputTypeList">
              <el-tag size="mini" v-for="(tag, i) in (fieldTemp.inputTypeList || '').split(',')" :key="i" style="margin: 2px;">{{ tag.trim() }}</el-tag>
            </div>
          </el-form-item>

          <!-- 自定义组件：Component 时显示 -->
          <template v-if="fieldTemp.type === 'Component'">
            <el-form-item label="选择组件">
              <el-select v-model="fieldTemp.component" style="width: 100%;" filterable allow-create placeholder="选择或输入组件名">
                <el-option-group label="基础组件">
                  <el-option label="申请人基本信息 (ApplicantInfo)" value="ApplicantInfo"></el-option>
                  <el-option label="印章选择 (SealSelector)" value="SealSelector"></el-option>
                </el-option-group>
                <el-option-group label="通用组件">
                  <el-option label="金额输入 (Money)" value="Money"></el-option>
                  <el-option label="银行卡信息 (BankCard)" value="BankCard"></el-option>
                  <el-option label="人脸识别 (FaceRecognition)" value="FaceRecognition"></el-option>
                  <el-option label="地址选择 (Address)" value="Address"></el-option>
                </el-option-group>
                <el-option-group label="文件相关">
                  <el-option label="带备注文件上传 (FileWithRemark)" value="FileWithRemark"></el-option>
                  <el-option label="模板文件 (TemplateFiles)" value="TemplateFiles"></el-option>
                </el-option-group>
                <el-option-group label="业务组件">
                  <el-option label="风控处理人 (RiskControlOperator)" value="RiskControlOperator"></el-option>
                  <el-option label="审批记录 (FlowLogs)" value="FlowLogs"></el-option>
                  <el-option label="还款计划表 (RepaymentList)" value="RepaymentList"></el-option>
                  <el-option label="利率输入 (InterestRate)" value="InterestRate"></el-option>
                </el-option-group>
              </el-select>
              <div class="field-hint">可选择预定义组件，也可输入自定义组件名</div>
            </el-form-item>
          </template>
        </div>

        <!-- 其他设置 -->
        <div class="field-dialog-section">
          <div class="field-dialog-section-title">其他设置</div>
          <el-row :gutter="16">
            <el-col :span="8">
              <el-form-item label="必填">
                <el-switch v-model="fieldTemp.isNotNull"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="只读">
                <el-switch v-model="fieldTemp.readOnly"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="服务端设值">
                <el-switch v-model="fieldTemp.setByServer"></el-switch>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="默认值">
            <el-input v-model="fieldTemp.defaultValue" placeholder="可选"></el-input>
          </el-form-item>
          <el-form-item label="描述">
            <el-input v-model="fieldTemp.desc" type="textarea" :rows="2" placeholder="可选"></el-input>
          </el-form-item>
        </div>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="fieldDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveField">确定</el-button>
      </div>
    </el-dialog>

    <!-- 添加节点对话框 -->
    <el-dialog title="添加节点" :visible.sync="nodeDialogVisible" width="450px" append-to-body>
      <el-form ref="nodeForm" :model="nodeTemp" :rules="nodeRules" label-width="100px" size="small">
        <el-form-item label="节点标识" prop="name">
          <el-input v-model="nodeTemp.name" placeholder="英文标识，如 deptApproval"></el-input>
        </el-form-item>
        <el-form-item label="节点名称" prop="title">
          <el-input v-model="nodeTemp.title" placeholder="显示名称，如 部门审批"></el-input>
        </el-form-item>
        <el-form-item label="节点类型">
          <el-radio-group v-model="nodeTemp.nodeType">
            <el-radio label="start">开始节点</el-radio>
            <el-radio label="approval">审批节点</el-radio>
            <el-radio label="gateway">网关</el-radio>
            <el-radio label="end">结束节点</el-radio>
          </el-radio-group>
          <div v-if="nodeTemp.nodeType === 'gateway'" style="margin-top: 8px; padding-left: 4px;">
            <el-radio-group v-model="nodeTemp.gatewayType" size="small">
              <el-radio-button label="exclusive">排他网关</el-radio-button>
              <el-radio-button label="parallel">并行网关</el-radio-button>
              <el-radio-button label="inclusive">包容网关</el-radio-button>
            </el-radio-group>
            <div style="font-size: 12px; color: #909399; margin-top: 4px;">
              <span v-if="nodeTemp.gatewayType === 'exclusive'">只走一条分支（条件互斥）</span>
              <span v-if="nodeTemp.gatewayType === 'parallel'">所有分支同时执行</span>
              <span v-if="nodeTemp.gatewayType === 'inclusive'">满足条件的分支都执行</span>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="nodeDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveNode">确定</el-button>
      </div>
    </el-dialog>

    <!-- 导入JSON对话框 -->
    <el-dialog title="导入JSON" :visible.sync="importDialogVisible" width="700px" append-to-body>
      <el-input
        v-model="importJsonText"
        type="textarea"
        :rows="15"
        placeholder="请粘贴 defJson 内容"
        style="font-family: monospace;"
      ></el-input>
      <div slot="footer">
        <el-button size="small" @click="importDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="doImport">导入</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import LogicFlow, { RectNode, RectNodeModel, CircleNode, CircleNodeModel, DiamondNode, DiamondNodeModel } from '@logicflow/core'
import '@logicflow/core/dist/index.css'

// ========== 自定义节点 ==========
const NODE_WIDTH = 140
const NODE_HEIGHT = 60

// 开始节点（绿色圆形）
class StartNodeModel extends CircleNodeModel {
  initNodeData(data) {
    super.initNodeData(data)
    this.r = 30
  }
  getNodeStyle() {
    const style = super.getNodeStyle()
    if (this.isSelected) {
      style.fill = '#52c41a'
      style.stroke = '#389e0d'
      style.strokeWidth = 2
    } else {
      style.fill = '#f6ffed'
      style.stroke = '#52c41a'
      style.strokeWidth = 1.5
    }
    return style
  }
  getTextStyle() {
    const style = super.getTextStyle()
    style.color = this.isSelected ? '#fff' : '#389e0d'
    style.fontSize = 12
    return style
  }
}

// 审批节点（蓝色圆角矩形）
class ApprovalNodeModel extends RectNodeModel {
  initNodeData(data) {
    super.initNodeData(data)
    this.width = NODE_WIDTH
    this.height = NODE_HEIGHT
    this.radius = 6
  }
  getNodeStyle() {
    const style = super.getNodeStyle()
    if (this.isSelected) {
      style.fill = '#1890ff'
      style.stroke = '#096dd9'
      style.strokeWidth = 2
    } else {
      style.fill = '#e6f7ff'
      style.stroke = '#1890ff'
      style.strokeWidth = 1
    }
    return style
  }
  getTextStyle() {
    const style = super.getTextStyle()
    style.color = this.isSelected ? '#fff' : '#096dd9'
    style.fontSize = 12
    return style
  }
}

// 结束节点（红色圆形）
class EndNodeModel extends CircleNodeModel {
  initNodeData(data) {
    super.initNodeData(data)
    this.r = 30
  }
  getNodeStyle() {
    const style = super.getNodeStyle()
    if (this.isSelected) {
      style.fill = '#ff4d4f'
      style.stroke = '#cf1322'
      style.strokeWidth = 2
    } else {
      style.fill = '#fff1f0'
      style.stroke = '#ff4d4f'
      style.strokeWidth = 1.5
    }
    return style
  }
  getTextStyle() {
    const style = super.getTextStyle()
    style.color = this.isSelected ? '#fff' : '#cf1322'
    style.fontSize = 12
    return style
  }
}

// 网关节点（橙色菱形）
class GatewayNodeModel extends DiamondNodeModel {
  initNodeData(data) {
    super.initNodeData(data)
    this.rx = 35
    this.ry = 35
  }
  getNodeStyle() {
    const style = super.getNodeStyle()
    if (this.isSelected) {
      style.fill = '#fa8c16'
      style.stroke = '#d46b08'
      style.strokeWidth = 2
    } else {
      style.fill = '#fff7e6'
      style.stroke = '#fa8c16'
      style.strokeWidth = 1.5
    }
    return style
  }
  getTextStyle() {
    const style = super.getTextStyle()
    style.color = this.isSelected ? '#fff' : '#d46b08'
    style.fontSize = 12
    style.overflowMode = 'autoWrap'
    return style
  }
}
import { getFlowDef, getFlowDefLog, getLatestFlowDefLog, saveFlowDefLog, updateFlowDefLog } from '../../api/workflow'
import { getAllUsers, getAllUserDeptRelations } from '../../api/user'
import { getAllRoles } from '../../api/role'
import { getAllDepartments } from '../../api/department'

export default {
  name: 'FlowDesigner',
  data() {
    return {
      flowDefId: null,
      flowDef: {},
      mode: 'copy', // 'edit' 编辑当前版本 | 'copy' 复制为新版本
      logId: null, // 指定加载的版本记录ID
      currentLogId: null, // 当前正在编辑的版本记录ID
      currentVersion: null, // 当前正在编辑的版本号
      defJson: {
        form: { fields: [] },
        nodes: []
      },
      lf: null,
      selectedNode: null,
      selectedLfNodeId: null,
      activeCollapse: ['basic', 'gateway', 'settings', 'operators', 'gotos', 'fieldsAccess'],
      // 表单字段
      formFields: [],
      treeProps: {
        label: 'title',
        children: 'fields'
      },
      // 字段编辑
      fieldDialogVisible: false,
      fieldDialogTitle: '添加字段',
      fieldTemp: {},
      fieldRules: {
        name: [{ required: true, message: '请输入字段标识', trigger: 'blur' }],
        title: [{ required: true, message: '请输入字段名称', trigger: 'blur' }]
      },
      fieldParent: null,
      editingField: null,
      // 节点编辑
      nodeDialogVisible: false,
      nodeTemp: { name: '', title: '', nodeType: 'approval' },
      nodeRules: {
        name: [{ required: true, message: '请输入节点标识', trigger: 'blur' }],
        title: [{ required: true, message: '请输入节点名称', trigger: 'blur' }]
      },
      // 导入
      importDialogVisible: false,
      importJsonText: '',
      // 下拉数据
      userList: [],
      roleList: [],
      departmentList: [],
      // 按部门分组的用户
      usersByDept: [],
      // 表单权限展开状态
      accessExpandMap: {},
      // 用于记录图形位置信息
      graphData: { nodes: [], edges: [] },
      // 面板宽度
      leftPanelWidth: 200,
      rightPanelWidth: 280,
      resizingPanel: null,
      resizeStartX: 0,
      resizeStartWidth: 0
    }
  },
  computed: {
    allNodes() {
      return this.defJson.nodes || []
    },
    inputTypeOptions() {
      const type = this.fieldTemp.type
      // 通用文本类输入
      const textInputs = [
        { label: '单行输入框 (el-input)', value: 'input' },
        { label: '多行文本框 (el-input textarea)', value: 'textarea' }
      ]
      // 数值类输入
      const numberInputs = [
        { label: '数字输入框 (el-input-number)', value: 'number' }
      ]
      // 选择类输入
      const selectInputs = [
        { label: '下拉选择 (el-select)', value: 'select' },
        { label: '单选框组 (el-radio-group)', value: 'radio' },
        { label: '多选框组 (el-checkbox-group)', value: 'checkbox' }
      ]
      // 日期类输入
      const dateInputs = [
        { label: '日期选择 (el-date-picker)', value: 'date' },
        { label: '日期范围 (el-date-picker range)', value: 'daterange' }
      ]
      // 文件类输入
      const fileInputs = [
        { label: '单文件上传 (el-upload)', value: 'file' },
        { label: '多文件上传 (el-upload multiple)', value: 'fileMore' }
      ]
      // 开关
      const boolInputs = [
        { label: '开关 (el-switch)', value: 'switch' },
        { label: '单选框组 (el-radio-group)', value: 'radio' }
      ]

      if (type === 'string') {
        return [...textInputs, ...selectInputs, ...fileInputs]
      }
      if (type === 'int' || type === 'long' || type === 'double') {
        return [...numberInputs, ...textInputs]
      }
      if (type === 'date') {
        return [
          { label: '日期选择 (el-date-picker)', value: 'date' }
        ]
      }
      if (type === 'datetime') {
        return [
          { label: '日期时间选择 (el-date-picker datetime)', value: 'datetime' },
          { label: '日期范围 (el-date-picker daterange)', value: 'daterange' }
        ]
      }
      if (type === 'boolean') {
        return [...boolInputs]
      }
      // Object / Array / Component 不需要输入方式
      return [...textInputs, ...numberInputs, ...selectInputs, ...dateInputs, ...fileInputs]
    }
  },
  created() {
    this.flowDefId = this.$route.query.id
    this.logId = this.$route.query.logId || null
    this.mode = this.$route.query.mode || 'copy'
    this.loadBaseData()
  },
  mounted() {
    this.$nextTick(() => {
      this.initLogicFlow()
      if (this.flowDefId) {
        this.loadFlowDef()
      }
    })
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onResizeMove)
    document.removeEventListener('mouseup', this.onResizeEnd)
    if (this._handleKeydown) {
      document.removeEventListener('keydown', this._handleKeydown)
    }
    if (this.lf) {
      this.lf.destroy()
      this.lf = null
    }
  },
  methods: {
    // ==================== 初始化 ====================
    async loadBaseData() {
      try {
        const [usersRes, rolesRes, deptsRes, relationsRes] = await Promise.all([
          getAllUsers().catch(() => ({ data: [] })),
          getAllRoles().catch(() => ({ data: [] })),
          getAllDepartments().catch(() => ({ data: [] })),
          getAllUserDeptRelations().catch(() => ({ data: [] }))
        ])
        this.userList = usersRes.data || []
        this.roleList = rolesRes.data || []
        this.departmentList = deptsRes.data || []
        const relations = relationsRes.data || []

        // 构建按部门分组的用户列表
        // deptId -> [userId, ...]
        const deptUserMap = {}
        const userDeptMap = {} // userId -> [deptId, ...]
        relations.forEach(r => {
          if (!deptUserMap[r.deptId]) deptUserMap[r.deptId] = []
          deptUserMap[r.deptId].push(r.userId)
          if (!userDeptMap[r.userId]) userDeptMap[r.userId] = []
          userDeptMap[r.userId].push(r.deptId)
        })

        // 构建 userMap 方便查找
        const userMap = {}
        this.userList.forEach(u => { userMap[u.id] = u })

        // 按部门分组，每个部门下挂用户
        this.usersByDept = this.departmentList.map(dept => {
          const uids = deptUserMap[dept.id] || []
          return {
            deptId: dept.id,
            deptName: dept.deptName,
            users: uids.map(uid => userMap[uid]).filter(Boolean)
          }
        }).filter(g => g.users.length > 0)

        // 未分配部门的用户
        const assignedUserIds = new Set(relations.map(r => r.userId))
        const unassignedUsers = this.userList.filter(u => !assignedUserIds.has(u.id))
        if (unassignedUsers.length > 0) {
          this.usersByDept.push({
            deptId: 0,
            deptName: '未分配部门',
            users: unassignedUsers
          })
        }
      } catch (e) {
        console.warn('加载基础数据失败', e)
      }
    },
    async loadFlowDef() {
      try {
        const res = await getFlowDef(this.flowDefId)
        this.flowDef = res.data || {}

        // 没有指定 logId 时，说明是新建版本，从空白开始
        if (!this.logId) return

        // 按指定版本记录ID加载
        const logRes = await getFlowDefLog(this.logId)
        const logData = logRes.data

        if (logData && logData.defJson) {
          this.currentLogId = logData.id
          this.currentVersion = logData.version
          const parsed = JSON.parse(logData.defJson)
          this.defJson = parsed
          this.formFields = this.addUids(parsed.form?.fields || [])

          // 优先用 shapeJson 还原画布（保留节点位置和连线路径）
          if (logData.shapeJson) {
            try {
              const shapeData = JSON.parse(logData.shapeJson)
              this.renderFromShapeJson(shapeData)
            } catch (_e) {
              this.renderFlowChart(true)
            }
          } else {
            this.renderFlowChart(true)
          }
        }
      } catch (e) {
        console.warn('加载流程定义失败', e)
      }
    },
    initLogicFlow() {
      const container = this.$refs.lfContainer
      if (!container) return

      this.lf = new LogicFlow({
        container,
        grid: {
          size: 20,
          visible: true,
          type: 'dot'
        },
        snapline: true,
        background: {
          backgroundColor: '#f5f5f5'
        },
        keyboard: {
          enabled: true
        },
        edgeType: 'polyline',
        adjustEdgeStartAndEnd: true,
        adjustEdgeMiddle: true
      })

      // 注册自定义节点类型
      this.lf.register('startNode', () => ({
        view: CircleNode,
        model: StartNodeModel
      }))
      this.lf.register('approvalNode', () => ({
        view: RectNode,
        model: ApprovalNodeModel
      }))
      this.lf.register('endNode', () => ({
        view: CircleNode,
        model: EndNodeModel
      }))
      this.lf.register('gatewayNode', () => ({
        view: DiamondNode,
        model: GatewayNodeModel
      }))

      this.lf.setDefaultEdgeType('polyline')

      // 绑定键盘 Delete/Backspace 删除选中元素
      this.lf.on('node:click', ({ data }) => {
        this._selectedElementId = data.id
        this._selectedElementType = 'node'
        this.handleNodeClick(data)
      })
      this.lf.on('edge:click', ({ data }) => {
        this._selectedElementId = data.id
        this._selectedElementType = 'edge'
        // 点击连线时，选中源节点以便查看其 gotos
        const sourceNode = this.defJson.nodes.find(n => n.name === data.sourceNodeId)
        if (sourceNode) {
          this.handleNodeClick({ id: sourceNode.name })
        }
      })
      this.lf.on('blank:click', () => {
        this._selectedElementId = null
        this._selectedElementType = null
        this.selectedNode = null
        this.selectedLfNodeId = null
      })

      // ========== 连线事件：画布 → 数据同步 ==========
      // 画布上新增连线 → 同步到 gotos
      this.lf.on('edge:add', ({ data }) => {
        if (this._syncingFromPanel) return // 避免循环触发
        const sourceNodeName = data.sourceNodeId
        const targetNodeName = data.targetNodeId
        const sourceNode = this.defJson.nodes.find(n => n.name === sourceNodeName)
        if (sourceNode) {
          if (!sourceNode.gotos) this.$set(sourceNode, 'gotos', [])
          // 检查是否已存在相同目标的 goto
          const exists = sourceNode.gotos.some(g => g.to === targetNodeName)
          if (!exists) {
            sourceNode.gotos.push({
              to: targetNodeName,
              name: data.text || '',
              conditionText: '',
              _edgeId: data.id
            })
          }
        }
      })

      // 画布上删除连线 → 同步从 gotos 移除
      this.lf.on('edge:delete', ({ data }) => {
        if (this._syncingFromPanel) return
        const sourceNodeName = data.sourceNodeId
        const targetNodeName = data.targetNodeId
        const sourceNode = this.defJson.nodes.find(n => n.name === sourceNodeName)
        if (sourceNode && sourceNode.gotos) {
          const idx = sourceNode.gotos.findIndex(g => g.to === targetNodeName && (g._edgeId === data.id || !g._edgeId))
          if (idx > -1) sourceNode.gotos.splice(idx, 1)
        }
      })

      // 画布上删除节点（任意方式触发）→ 同步从 defJson 移除，并清理所有 gotos 中对已删节点的引用
      this.lf.on('node:delete', ({ data }) => {
        if (this._syncingFromPanel) return
        const nodeId = data && (data.id || data)
        if (!nodeId) return
        const idx = this.defJson.nodes.findIndex(n => n.name === nodeId)
        if (idx > -1) this.defJson.nodes.splice(idx, 1)
        this.defJson.nodes.forEach(n => {
          if (n.gotos) {
            n.gotos = n.gotos.filter(g => g.to !== nodeId)
          }
        })
        if (this.selectedNode && this.selectedNode.name === nodeId) {
          this.selectedNode = null
          this.selectedLfNodeId = null
        }
        if (this._selectedElementId === nodeId) {
          this._selectedElementId = null
          this._selectedElementType = null
        }
      })

      // 画布上编辑连线文字 → 同步到 gotos 的 name
      this.lf.on('text:update', ({ data }) => {
        if (this._syncingFromPanel) return
        // data 包含 id 和 text，查找是否为 edge
        const graphData = this.lf.getGraphData()
        const edge = (graphData.edges || []).find(e => e.id === data.id)
        if (edge) {
          const sourceNode = this.defJson.nodes.find(n => n.name === edge.sourceNodeId)
          if (sourceNode && sourceNode.gotos) {
            const gt = sourceNode.gotos.find(g => g._edgeId === data.id)
            if (gt) {
              const newText = typeof data.text === 'string' ? data.text : (data.text && data.text.value ? data.text.value : '')
              gt.name = newText
            }
          }
        }
      })

      // 监听键盘删除
      this._handleKeydown = (e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          // 避免在输入框中误删
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
          if (this._selectedElementId) {
            if (this._selectedElementType === 'node') {
              this.lf.deleteNode(this._selectedElementId)
              // 同步删除数据
              const idx = this.defJson.nodes.findIndex(n => n.name === this._selectedElementId)
              if (idx > -1) this.defJson.nodes.splice(idx, 1)
              // 清除其他节点 gotos 中指向该节点的分支
              this.defJson.nodes.forEach(n => {
                if (n.gotos) {
                  n.gotos = n.gotos.filter(g => g.to !== this._selectedElementId)
                }
              })
              if (this.selectedNode && this.selectedNode.name === this._selectedElementId) {
                this.selectedNode = null
              }
            } else if (this._selectedElementType === 'edge') {
              // edge:delete 事件会自动触发同步 gotos
              this.lf.deleteEdge(this._selectedElementId)
            }
            this._selectedElementId = null
            this._selectedElementType = null
          }
        }
      }
      document.addEventListener('keydown', this._handleKeydown)

      this.lf.render({})
    },

    // ==================== 流程图渲染 ====================
    renderFlowChart(autoFit) {
      if (!this.lf) return
      const nodes = this.defJson.nodes || []
      if (nodes.length === 0) return

      const lfNodes = []
      const lfEdges = []
      const startX = 350
      let y = 60

      nodes.forEach((node, index) => {
        // 根据节点的 nodeType 字段确定图形类型，兼容旧数据用 name/title 推断
        let nodeType = 'approvalNode'
        if (node.nodeType === 'start') {
          nodeType = 'startNode'
        } else if (node.nodeType === 'end') {
          nodeType = 'endNode'
        } else if (node.nodeType === 'gateway') {
          nodeType = 'gatewayNode'
        } else if (!node.nodeType) {
          // 兼容旧数据：按 name/title/index 推断
          const isStart = node.name === 'start' || index === 0
          const isEnd = node.name === 'end' || node.title === '结束'
          nodeType = isStart ? 'startNode' : (isEnd ? 'endNode' : 'approvalNode')
        }
        lfNodes.push({
          id: node.name,
          type: nodeType,
          x: startX,
          y: y,
          text: node.title,
          properties: {}
        })

        y += 120
      })

      // 生成连线，同时回填 _edgeId
      this._syncingFromPanel = true
      nodes.forEach(node => {
        if (node.gotos && node.gotos.length > 0) {
          node.gotos.forEach((goto, gotoIndex) => {
            if (goto.to) {
              const edgeId = `${node.name}_to_${goto.to}_${gotoIndex}`
              goto._edgeId = edgeId
              lfEdges.push({
                id: edgeId,
                type: 'polyline',
                sourceNodeId: node.name,
                targetNodeId: goto.to,
                text: goto.name || ''
              })
            }
          })
        }
      })

      this.lf.render({ nodes: lfNodes, edges: lfEdges })
      this._syncingFromPanel = false
      // 渲染后重置为1:1缩放并居中，不自动放大
      setTimeout(() => {
        if (autoFit) {
          this.lf.fitView(80, 80)
          // 如果缩放比例大于1（即被放大了），重置为1
          const zoom = this.lf.getTransform()
          if (zoom && zoom.SCALE_X > 1) {
            this.lf.resetZoom()
            this.lf.translateCenter()
          }
        } else {
          this.lf.resetZoom()
          this.lf.translateCenter()
        }
      }, 100)
    },
    // 从保存的 shapeJson 还原画布（精确恢复节点位置和连线路径）
    renderFromShapeJson(shapeData) {
      if (!this.lf) return
      this._syncingFromPanel = true

      const defNodes = this.defJson.nodes || []

      // 确保 shapeJson 中的节点 id 和类型与 defJson 一致
      const shapeNodes = shapeData.nodes || []
      shapeNodes.forEach(sn => {
        // 按 id 或 text 匹配 defJson 节点，确保 id 一致
        let defNode = defNodes.find(dn => dn.name === sn.id)
        if (!defNode) {
          const snText = typeof sn.text === 'string' ? sn.text : (sn.text && sn.text.value ? sn.text.value : '')
          defNode = defNodes.find(dn => dn.title === snText)
          if (defNode) {
            // 修正 shapeJson 中的 id 为 defJson 的 name
            const oldId = sn.id
            sn.id = defNode.name
            // 同步修正连线中的 sourceNodeId / targetNodeId
            ;(shapeData.edges || []).forEach(e => {
              if (e.sourceNodeId === oldId) e.sourceNodeId = defNode.name
              if (e.targetNodeId === oldId) e.targetNodeId = defNode.name
            })
          }
        }
        // 确保节点类型正确（兼容旧 shapeJson 没有 gatewayNode 的情况）
        if (defNode) {
          const nt = defNode.nodeType
          const correctType = nt === 'start' ? 'startNode' : nt === 'end' ? 'endNode' : nt === 'gateway' ? 'gatewayNode' : 'approvalNode'
          if (sn.type !== correctType) {
            sn.type = correctType
          }
        }
      })

      // 回填 gotos 中的 _edgeId
      const edges = shapeData.edges || []
      defNodes.forEach(node => {
        if (node.gotos) {
          node.gotos.forEach(gt => {
            const matchedEdge = edges.find(e =>
              e.sourceNodeId === node.name && e.targetNodeId === gt.to && !e._matched
            )
            if (matchedEdge) {
              gt._edgeId = matchedEdge.id
              matchedEdge._matched = true
            }
          })
        }
      })
      // 清除临时标记
      edges.forEach(e => { delete e._matched })

      this.lf.render(shapeData)
      this._syncingFromPanel = false

      setTimeout(() => {
        this.lf.fitView(80, 80)
        const zoom = this.lf.getTransform()
        if (zoom && zoom.SCALE_X > 1) {
          this.lf.resetZoom()
          this.lf.translateCenter()
        }
      }, 100)
    },

    handleNodeClick(lfNodeData) {
      const nodeId = lfNodeData.id
      this.selectedLfNodeId = nodeId
      // 先按 name 匹配，找不到时按 text/title 匹配（兼容 shapeJson 恢复后 id 不一致的情况）
      let node = this.defJson.nodes.find(n => n.name === nodeId)
      if (!node && lfNodeData.text) {
        const text = typeof lfNodeData.text === 'string' ? lfNodeData.text : (lfNodeData.text.value || '')
        node = this.defJson.nodes.find(n => n.title === text)
      }
      if (node) {
        // 确保字段有默认值
        if (!node.gotos) this.$set(node, 'gotos', [])
        if (node.nodeType === 'gateway') {
          // 网关节点只需 gatewayType 和 gotos
          if (!node.gatewayType) this.$set(node, 'gatewayType', 'exclusive')
        } else {
          // 非网关节点
          if (!node.operators) this.$set(node, 'operators', [])
          if (!node.fieldsAccess) this.$set(node, 'fieldsAccess', {})
          if (!node.viewers) this.$set(node, 'viewers', [])
          if (node.operatorType === undefined) this.$set(node, 'operatorType', 'one')
          if (node.canSubmit === undefined) this.$set(node, 'canSubmit', true)
          if (node.canReject === undefined) this.$set(node, 'canReject', false)
          if (node.canEnd === undefined) this.$set(node, 'canEnd', false)
          // 兼容旧配置：布尔值 true 转换为 'record'
          if (node.autoSubmit === true) {
            this.$set(node, 'autoSubmit', 'record')
          } else if (node.autoSubmit === undefined || node.autoSubmit === false) {
            this.$set(node, 'autoSubmit', '')
          }
          if (node.skipDuplicateOperator === undefined) this.$set(node, 'skipDuplicateOperator', '')
        }
        this.selectedNode = node
      }
    },
    handleNodeDelete(lfNodeData) {
      const index = this.defJson.nodes.findIndex(n => n.name === lfNodeData.id)
      if (index > -1) {
        this.defJson.nodes.splice(index, 1)
        if (this.selectedNode && this.selectedNode.name === lfNodeData.id) {
          this.selectedNode = null
        }
      }
    },
    onNodeNameChange() {
      // 名称变更时同步到图形
      // 此处简化处理，重新渲染
    },
    onNodeTitleChange() {
      if (this.lf && this.selectedLfNodeId) {
        this.lf.updateText(this.selectedLfNodeId, this.selectedNode.title)
      }
    },

    // ==================== 工具栏 ====================
    handleBack() {
      this.$router.push('/workflow/list')
    },
    handleZoomIn() {
      this.lf && this.lf.zoom(true)
    },
    handleZoomOut() {
      this.lf && this.lf.zoom(false)
    },
    handleFitView() {
      this.lf && this.lf.fitView(80, 80)
    },
    handleImport() {
      this.importJsonText = ''
      this.importDialogVisible = true
    },
    doImport() {
      try {
        const json = JSON.parse(this.importJsonText)
        if (!json.form || !json.nodes) {
          this.$message.error('JSON 格式不正确，需要包含 form 和 nodes')
          return
        }
        this.defJson = json
        this.formFields = this.addUids(json.form?.fields || [])
        this.renderFlowChart(true)
        this.selectedNode = null
        this.importDialogVisible = false
        this.$message.success('导入成功')
      } catch (e) {
        this.$message.error('JSON 解析失败：' + e.message)
      }
    },
    // 生成干净的 defJson（去除内部标记字段如 _edgeId, _uid）
    buildCleanDefJson() {
      const cleanNodes = (this.defJson.nodes || []).map(node => {
        const n = { ...node }
        if (n.gotos) {
          n.gotos = n.gotos.map(g => {
            const { _edgeId, ...rest } = g
            return rest
          })
        }
        return n
      })
      return {
        form: { fields: this.stripUids(this.formFields) },
        nodes: cleanNodes
      }
    },
    handleExport() {
      const cleanJson = this.buildCleanDefJson()
      const jsonStr = JSON.stringify(cleanJson, null, 2)
      const blob = new Blob([jsonStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${this.flowDef.code || 'flow'}_defJson.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.$message.success('导出成功')
    },
    askOperationPassword() {
      return this.$prompt('请输入操作密码', '安全验证', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'password',
        inputPlaceholder: '操作密码',
        inputValidator: (val) => !!val || '请输入操作密码'
      }).then(({ value }) => value)
    },
    async handleSave() {
      const cleanJson = this.buildCleanDefJson()
      const defJsonStr = JSON.stringify(cleanJson)
      // 获取画布图形数据（节点位置、连线路径）
      const shapeJsonStr = this.lf ? JSON.stringify(this.lf.getGraphData()) : ''
      try {
        const operationPassword = await this.askOperationPassword()
        if (this.mode === 'edit' && this.currentLogId) {
          // 编辑模式：原地更新当前版本，不产生新版本
          await updateFlowDefLog(this.currentLogId, { defJson: defJsonStr, shapeJson: shapeJsonStr }, operationPassword)
          this.$message.success('保存成功，版本 V' + this.currentVersion + ' 已更新')
        } else {
          // 复制/新建模式：创建新版本，不自动应用
          const res = await saveFlowDefLog({
            flowDefId: this.flowDefId,
            defJson: defJsonStr,
            shapeJson: shapeJsonStr
          }, operationPassword)
          const newVersion = res.data.version
          this.currentLogId = res.data.id
          this.currentVersion = newVersion
          // 保存后切换为编辑模式，后续保存都是编辑当前版本
          this.mode = 'edit'
          this.$message.success('已保存为新版本 V' + newVersion + '（需在版本管理中手动应用）')
        }
      } catch (e) {
        this.$message.error('保存失败')
      }
    },

    // ==================== 删除选中 ====================
    handleDeleteSelected() {
      if (!this._selectedElementId) {
        this.$message.warning('请先点击选中一个节点或连线')
        return
      }
      const type = this._selectedElementType === 'node' ? '节点' : '连线'
      this.$confirm(`确定要删除选中的${type}吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        if (this._selectedElementType === 'node') {
          const deletedName = this._selectedElementId
          this.lf.deleteNode(deletedName)
          const idx = this.defJson.nodes.findIndex(n => n.name === deletedName)
          if (idx > -1) this.defJson.nodes.splice(idx, 1)
          // 清除其他节点 gotos 中指向该节点的分支
          this.defJson.nodes.forEach(n => {
            if (n.gotos) {
              n.gotos = n.gotos.filter(g => g.to !== deletedName)
            }
          })
          if (this.selectedNode && this.selectedNode.name === deletedName) {
            this.selectedNode = null
          }
        } else if (this._selectedElementType === 'edge') {
          // edge:delete 事件会自动同步 gotos
          this.lf.deleteEdge(this._selectedElementId)
        }
        this._selectedElementId = null
        this._selectedElementType = null
        this.$message.success('删除成功')
      })
    },

    // ==================== 节点操作 ====================
    handleAddNode() {
      this.nodeTemp = { name: '', title: '', nodeType: 'approval', gatewayType: 'exclusive' }
      this.nodeDialogVisible = true
    },
    saveNode() {
      this.$refs.nodeForm.validate(valid => {
        if (!valid) return
        // 检查重名
        if (this.defJson.nodes.some(n => n.name === this.nodeTemp.name)) {
          this.$message.error('节点标识已存在')
          return
        }
        const isGateway = this.nodeTemp.nodeType === 'gateway'
        const newNode = {
          name: this.nodeTemp.name,
          title: this.nodeTemp.title,
          nodeType: this.nodeTemp.nodeType,
          ...(isGateway ? { gatewayType: this.nodeTemp.gatewayType } : {}),
          operatorType: isGateway ? undefined : 'one',
          canSubmit: isGateway ? undefined : true,
          canReject: this.nodeTemp.nodeType === 'approval',
          canEnd: isGateway ? undefined : false,
          operators: isGateway ? undefined : [],
          gotos: [],
          fieldsAccess: isGateway ? undefined : {},
          viewers: isGateway ? undefined : []
        }
        // 添加到数据中
        this.defJson.nodes.push(newNode)

        // 只在画布上增量添加新节点，不重新渲染整个画布
        const existingNodes = this.lf.getGraphData().nodes || []
        let maxY = 60
        existingNodes.forEach(n => {
          if (n.y > maxY) maxY = n.y
        })

        const nt = this.nodeTemp.nodeType
        const nodeType = nt === 'start' ? 'startNode' : nt === 'end' ? 'endNode' : nt === 'gateway' ? 'gatewayNode' : 'approvalNode'

        this.lf.addNode({
          id: newNode.name,
          type: nodeType,
          x: 350,
          y: maxY + 120,
          text: newNode.title,
          properties: {}
        })

        this.nodeDialogVisible = false
        this.$message.success('节点添加成功')
      })
    },
    addOperator() {
      if (!this.selectedNode) return
      this.selectedNode.operators.push({
        userId: '',
        role: '',
        department: '',
        departmentType: '',
        userFromVariable: false,
        userVariable: ''
      })
    },
    removeOperator(index) {
      this.selectedNode.operators.splice(index, 1)
    },
    addGoto() {
      if (!this.selectedNode) return
      this.selectedNode.gotos.push({
        to: '',
        name: '',
        conditionText: '',
        _edgeId: ''
      })
    },
    removeGoto(index) {
      if (!this.selectedNode) return
      const gt = this.selectedNode.gotos[index]
      // 删除画布上对应的连线
      if (gt._edgeId && this.lf) {
        this._syncingFromPanel = true
        try { this.lf.deleteEdge(gt._edgeId) } catch (_e) { /* ignore */ }
        this._syncingFromPanel = false
      } else if (gt.to && this.lf) {
        // 没有 _edgeId 时，按 source/target 查找并删除
        this._syncingFromPanel = true
        this.removeEdgeBySourceTarget(this.selectedNode.name, gt.to)
        this._syncingFromPanel = false
      }
      this.selectedNode.gotos.splice(index, 1)
    },
    // 右侧修改 goto 目标节点 → 画布同步连线
    onGotoTargetChange(gt, newTarget, _index) {
      if (!this.lf || !this.selectedNode) return
      this._syncingFromPanel = true
      // 先删掉旧连线
      if (gt._edgeId) {
        try { this.lf.deleteEdge(gt._edgeId) } catch (_e) { /* ignore */ }
        gt._edgeId = ''
      }
      // 添加新连线
      if (newTarget) {
        try {
          const edgeData = this.lf.addEdge({
            type: 'polyline',
            sourceNodeId: this.selectedNode.name,
            targetNodeId: newTarget,
            text: gt.name || ''
          })
          gt._edgeId = edgeData.id
        } catch (_e) {
          console.warn('添加连线失败', _e)
        }
      }
      this._syncingFromPanel = false
    },
    // 右侧修改 goto 名称 → 更新画布连线文字
    onGotoNameChange(gt) {
      if (!this.lf || !gt._edgeId) return
      this._syncingFromPanel = true
      try {
        // 直接通过 graphModel 更新边的文字，不删除重建
        const edgeModel = this.lf.graphModel.getEdgeModelById(gt._edgeId)
        if (edgeModel) {
          edgeModel.updateText(gt.name || '')
        }
      } catch (_e) {
        console.warn('更新连线文字失败', _e)
      }
      this._syncingFromPanel = false
    },
    // 按源节点和目标节点删除连线
    removeEdgeBySourceTarget(sourceId, targetId) {
      if (!this.lf) return
      const graphData = this.lf.getGraphData()
      const edges = graphData.edges || []
      edges.forEach(edge => {
        if (edge.sourceNodeId === sourceId && edge.targetNodeId === targetId) {
          try { this.lf.deleteEdge(edge.id) } catch (_e) { /* ignore */ }
        }
      })
    },
    addViewer() {
      if (!this.selectedNode) return
      if (!this.selectedNode.viewers) this.$set(this.selectedNode, 'viewers', [])
      this.selectedNode.viewers.push({ role: '', department: '' })
    },
    removeViewer(index) {
      this.selectedNode.viewers.splice(index, 1)
    },

    // ==================== 表单权限 ====================
    toggleAccessExpand(groupName) {
      this.$set(this.accessExpandMap, groupName, !this.accessExpandMap[groupName])
    },
    getAccess(fieldName, prop) {
      if (!this.selectedNode || !this.selectedNode.fieldsAccess) return false
      const access = this.selectedNode.fieldsAccess[fieldName]
      return access ? !!access[prop] : false
    },
    getFieldAccess(groupName, fieldName, prop) {
      if (!this.selectedNode || !this.selectedNode.fieldsAccess) return false
      const groupAccess = this.selectedNode.fieldsAccess[groupName]
      if (!groupAccess || !groupAccess.fields) return false
      const fieldAccess = groupAccess.fields[fieldName]
      return fieldAccess ? !!fieldAccess[prop] : false
    },
    setFieldAccess(groupName, fieldName, prop, val) {
      if (!this.selectedNode) return
      if (!this.selectedNode.fieldsAccess) {
        this.$set(this.selectedNode, 'fieldsAccess', {})
      }
      if (!this.selectedNode.fieldsAccess[groupName]) {
        this.$set(this.selectedNode.fieldsAccess, groupName, {})
      }
      if (!this.selectedNode.fieldsAccess[groupName].fields) {
        this.$set(this.selectedNode.fieldsAccess[groupName], 'fields', {})
      }
      if (!this.selectedNode.fieldsAccess[groupName].fields[fieldName]) {
        this.$set(this.selectedNode.fieldsAccess[groupName].fields, fieldName, {})
      }
      this.$set(this.selectedNode.fieldsAccess[groupName].fields[fieldName], prop, val)
    },
    setAllGroupsAccess(visible) {
      if (!this.selectedNode) return
      if (!this.selectedNode.fieldsAccess) {
        this.$set(this.selectedNode, 'fieldsAccess', {})
      }
      this.formFields.forEach(group => {
        if (!this.selectedNode.fieldsAccess[group.name]) {
          this.$set(this.selectedNode.fieldsAccess, group.name, {})
        }
        this.$set(this.selectedNode.fieldsAccess[group.name], 'visible', visible)
      })
    },
    setAccess(fieldName, prop, val) {
      if (!this.selectedNode) return
      if (!this.selectedNode.fieldsAccess) {
        this.$set(this.selectedNode, 'fieldsAccess', {})
      }
      if (!this.selectedNode.fieldsAccess[fieldName]) {
        this.$set(this.selectedNode.fieldsAccess, fieldName, {})
      }
      this.$set(this.selectedNode.fieldsAccess[fieldName], prop, val)
    },

    // ==================== 表单字段操作 ====================
    onFieldTypeChange(type) {
      // 切换数据类型时，自动设定合理的默认输入方式
      if (type === 'string') {
        this.fieldTemp.inputType = 'input'
      } else if (type === 'int' || type === 'long') {
        this.fieldTemp.inputType = 'number'
      } else if (type === 'double') {
        this.fieldTemp.inputType = 'number'
      } else if (type === 'boolean') {
        this.fieldTemp.inputType = 'switch'
      } else if (type === 'date') {
        this.fieldTemp.inputType = 'date'
      } else if (type === 'datetime') {
        this.fieldTemp.inputType = 'datetime'
      } else if (type === 'Object' || type === 'Array') {
        this.fieldTemp.inputType = ''
        this.fieldTemp.component = ''
      } else if (type === 'Component') {
        this.fieldTemp.inputType = ''
        this.fieldTemp.component = this.fieldTemp.component || ''
      }
    },
    handleAddFieldGroup() {
      this.fieldDialogTitle = '添加字段组'
      this.fieldTemp = {
        name: '', title: '', type: 'Object', inputType: '',
        isNotNull: false, defaultValue: '', desc: '', inputTypeList: '', component: ''
      }
      this.fieldParent = null
      this.editingField = null
      this.fieldDialogVisible = true
    },
    handleAddField(parent) {
      this.fieldDialogTitle = '添加子字段'
      this.fieldTemp = {
        name: '', title: '', type: 'string', inputType: 'input',
        isNotNull: false, defaultValue: '', desc: '', inputTypeList: '', component: ''
      }
      this.fieldParent = parent
      this.editingField = null
      this.fieldDialogVisible = true
    },
    handleEditField(field) {
      this.fieldDialogTitle = '编辑字段'
      this.fieldTemp = { ...field, fields: undefined, component: field.component || '' }
      this.editingField = field
      this.fieldParent = null
      this.fieldDialogVisible = true
    },
    handleDeleteField(field) {
      this.$confirm(`确定要删除字段「${field.title}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.removeFieldFromTree(this.formFields, field._uid)
        this.$message.success('删除成功')
      })
    },
    removeFieldFromTree(fields, uid) {
      const index = fields.findIndex(f => f._uid === uid)
      if (index > -1) {
        fields.splice(index, 1)
        return true
      }
      for (const f of fields) {
        if (f.fields && this.removeFieldFromTree(f.fields, uid)) return true
      }
      return false
    },
    saveField() {
      this.$refs.fieldForm.validate(valid => {
        if (!valid) return
        if (this.editingField) {
          // 编辑模式
          const preservedFields = this.editingField.fields
          Object.assign(this.editingField, this.fieldTemp)
          if (preservedFields) this.editingField.fields = preservedFields
        } else {
          const newField = { ...this.fieldTemp, _uid: this.genUid() }
          if (newField.type === 'Object' || newField.type === 'Array') {
            newField.fields = []
          }
          if (this.fieldParent) {
            if (!this.fieldParent.fields) this.$set(this.fieldParent, 'fields', [])
            this.fieldParent.fields.push(newField)
          } else {
            this.formFields.push(newField)
          }
        }
        this.fieldDialogVisible = false
        this.$message.success('保存成功')
      })
    },
    // ==================== 表单字段拖拽排序 ====================
    handleAllowDrag() {
      return true
    },
    handleAllowDrop(draggingNode, dropNode, type) {
      // 只允许同级拖拽排序，不允许拖入其他节点内部
      if (type === 'inner') return false
      // 必须在同一个父节点下
      return draggingNode.parent === dropNode.parent
    },

    getFieldIcon(data) {
      if (data.type === 'Object') return 'el-icon-folder-opened'
      if (data.type === 'Array') return 'el-icon-files'
      if (data.type === 'Component') return 'el-icon-s-grid'
      if (data.inputType === 'file' || data.inputType === 'fileMore') return 'el-icon-upload'
      if (data.inputType === 'select' || data.inputType === 'radio') return 'el-icon-arrow-down'
      if (data.inputType === 'checkbox') return 'el-icon-finished'
      if (data.inputType === 'switch') return 'el-icon-open'
      if (data.inputType === 'date' || data.inputType === 'daterange') return 'el-icon-date'
      if (data.inputType === 'number') return 'el-icon-s-data'
      if (data.inputType === 'textarea') return 'el-icon-document'
      return 'el-icon-edit-outline'
    },

    // ==================== 工具方法 ====================
    genUid() {
      return '_' + Math.random().toString(36).substr(2, 9)
    },
    addUids(fields) {
      return (fields || []).map(f => {
        const item = { ...f, _uid: this.genUid() }
        if (f.fields) {
          item.fields = this.addUids(f.fields)
        }
        return item
      })
    },
    stripUids(fields) {
      return (fields || []).map(f => {
        const item = { ...f }
        delete item._uid
        if (f.fields) {
          item.fields = this.stripUids(f.fields)
        }
        return item
      })
    },

    // ==================== 面板拖拽调节 ====================
    startResizeLeft(e) {
      this.resizingPanel = 'left'
      this.resizeStartX = e.clientX
      this.resizeStartWidth = this.leftPanelWidth
      document.addEventListener('mousemove', this.onResizeMove)
      document.addEventListener('mouseup', this.onResizeEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    startResizeRight(e) {
      this.resizingPanel = 'right'
      this.resizeStartX = e.clientX
      this.resizeStartWidth = this.rightPanelWidth
      document.addEventListener('mousemove', this.onResizeMove)
      document.addEventListener('mouseup', this.onResizeEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    onResizeMove(e) {
      if (!this.resizingPanel) return
      const delta = e.clientX - this.resizeStartX
      if (this.resizingPanel === 'left') {
        const newWidth = this.resizeStartWidth + delta
        this.leftPanelWidth = Math.max(150, Math.min(400, newWidth))
      } else {
        const newWidth = this.resizeStartWidth - delta
        this.rightPanelWidth = Math.max(200, Math.min(500, newWidth))
      }
    },
    onResizeEnd() {
      this.resizingPanel = null
      document.removeEventListener('mousemove', this.onResizeMove)
      document.removeEventListener('mouseup', this.onResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      // 触发 LogicFlow 重新计算画布大小
      if (this.lf) {
        this.lf.resize()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.flow-designer {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
}

.toolbar {
  height: 48px;
  background: #fff;
  border-bottom: 1px solid #dcdfe6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex-shrink: 0;

  .toolbar-left {
    display: flex;
    align-items: center;

    .flow-title {
      margin-left: 16px;
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }
  }

  .toolbar-right .el-button {
    margin-left: 8px;
  }
}

.designer-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* 左侧面板 */
.left-panel {
  background: #fff;
  border-right: 1px solid #dcdfe6;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-width: 150px;
  max-width: 400px;
}

/* 右侧面板 */
.right-panel {
  background: #fff;
  border-left: 1px solid #dcdfe6;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-width: 200px;
  max-width: 500px;

  .panel-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

/* 拖拽手柄 */
.resize-handle {
  width: 4px;
  cursor: col-resize;
  background: transparent;
  flex-shrink: 0;
  position: relative;
  z-index: 10;

  &:hover,
  &:active {
    background: #409eff;
    opacity: 0.3;
  }
}

.panel-header {
  height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  flex-shrink: 0;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;

  i { font-size: 48px; margin-bottom: 12px; }
  p { font-size: 13px; }
}

/* 中间画布 */
.center-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.canvas-toolbar {
  height: 40px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  padding: 0 12px;
  justify-content: space-between;
  flex-shrink: 0;
}

.lf-container {
  flex: 1;
  width: 100%;
}

/* 树节点 */
.tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 8px;
  font-size: 13px;

  .node-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .node-actions {
    display: none;
    white-space: nowrap;

    i {
      margin-left: 6px;
      font-size: 13px;
      cursor: pointer;
      color: #409eff;

      &:hover { color: #66b1ff; }
      &.el-icon-delete {
        color: #f56c6c;
        &:hover { color: #f78989; }
      }
    }
  }

  &:hover .node-actions {
    display: inline-flex;
  }
}

/* 配置卡片 */
.config-card {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 8px;

  .config-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #606266;
  }

  .delete-btn {
    color: #f56c6c;
    padding: 0;
  }

  .el-form-item {
    margin-bottom: 8px;
  }
}

/* 表单权限 */
.access-quick-actions {
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
}

.access-group {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child { border-bottom: none; }
}

.access-group-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;

  &:hover { background: #f5f7fa; border-radius: 3px; }

  .access-expand-icon {
    width: 18px;
    font-size: 12px;
    color: #909399;
    flex-shrink: 0;
  }

  .access-group-title-text {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
    flex: 1;
  }

  .access-group-tag {
    font-size: 11px;
    color: #909399;
    margin-left: 4px;
  }
}

.access-options {
  .el-checkbox { margin-right: 8px; }
}

.access-options-group {
  padding-left: 18px;
  margin-bottom: 4px;
}

.access-children {
  padding-left: 18px;
  border-left: 2px solid #e6e8eb;
  margin-left: 8px;
  margin-bottom: 4px;
}

.access-field {
  padding: 3px 0 3px 6px;

  &:hover { background: #fafafa; border-radius: 3px; }
}

.access-field-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;

  .access-field-name {
    color: #c0c4cc;
    font-size: 11px;
    margin-left: 2px;
  }
}

.access-options-field {
  padding-left: 0;

  .el-checkbox {
    margin-right: 6px;
  }
}

.field-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

/* 覆盖 Element UI 折叠面板样式 */
::v-deep .el-collapse-item__header {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  padding-left: 4px;
}

::v-deep .el-collapse-item__content {
  padding-bottom: 8px;
}

::v-deep .el-form-item {
  margin-bottom: 12px;
}

::v-deep .el-form-item__label {
  font-size: 12px;
  color: #606266;
  padding-bottom: 2px;
}

/* 字段编辑弹窗分区 */
.field-dialog-section {
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.field-dialog-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}

.field-dialog-tags {
  margin-top: 6px;
}
</style>
